import { lazy } from "react";
import {
  Car,
  BarChart2,
  Settings,
  ClipboardList,
  CarFront,
  ChartColumnBig,
} from "lucide-react";
import { App, AppKey } from "./types/app";

export const APPS: Record<AppKey, App> = {
  garageregister: {
    key: "garageregister",
    name: "Garage Register",
    basePath: "/garage-register",
    plan: "Enterprise",
    routes: [
      {
        path: "/garage-register",
        component: lazy(() => import("@/app/apps/garage-register/page")),
        nav: { title: "Garage Register", icon: Car },
        permission: { entityType: "garageregister", action: "view_vehicle" },
      },
      {
        path: "/garage-register/reporting",
        component: lazy(
          () => import("@/app/apps/garage-register/reporting/page")
        ),
        nav: { title: "Reporting", icon: BarChart2 },
        permission: { entityType: "garageregister", action: "view_omvic" },
      },
      {
        path: "/garage-register/analytics",
        component: lazy(
          () => import("@/app/apps/garage-register/analytics/page")
        ),
        nav: { title: "Analytics", icon: ChartColumnBig },
        permission: { entityType: "garageregister", action: "view_omvic" },
      },
      {
        path: "/garage-register/logs",
        component: lazy(
          () => import("@/app/apps/garage-register/garage-register-logs/page")
        ),
        nav: { title: "Logs", icon: ClipboardList },
        permission: { entityType: "garageregister",   action: [
      "view_garage_stock_history",
      "view_garage_management_history"
    ] },
      },{
        path: "/garage-register/management",
        component: lazy(
          () => import("@/app/apps/garage-register/management/page")
        ),
        nav: { title: "Management", icon: Settings },
        permission: {
          entityType: "garageregister",
          action: "manage_garageregister",
          // group: "service_advisor",
        },
      },
      {
        path: "/garage-register/stock-in",
        component: lazy(
          () => import("@/app/apps/garage-register/stock-in/page")
        ),
        permission: { entityType: "garageregister", action: "add_vehicle" },
      },
      {
        path: "/garage-register/stock-out",
        component: lazy(
          () => import("@/app/apps/garage-register/stock-out/page")
        ),
        permission: { entityType: "garageregister", action: "delete_vehicle" },
      },
    ],
  },
  getready: {
    key: "getready",
    name: "Get Ready",
    basePath: "/get-ready",
    plan: "Enterprise",
    // Get Ready entities live under the `dealership` entity_type on
    // the backend (post-migration to the unified `dealership` Django
    // app). The frontend app key stays "getready" — it's a UI/route
    // identifier — but entity-switcher scoping uses this override.
    entityType: "dealership",
    // Permission codenames match the backend's HasScopedPermission
    // checks (see PAG-backend/dealership/views.py). Without one of
    // these on a user's scoped permissions for a dealership, the
    // route is filtered out of the sidebar AND ProtectedRoute
    // redirects to /unauthorized on direct nav.
    //
    // Codename map (post-migration):
    //   • view_getreadyrequest   → module access (list/detail)
    //   • manage_getreadyrequest → write access (services, announcements)
    //   • add_getreadyrequest    → create new requests
    routes: [
      {
        path: "/get-ready",
        component: lazy(() => import("@/app/apps/get-ready/page")),
        nav: { title: "Get Ready", icon: Car },
        permission: {
          entityType: "dealership",
          action: "view_getreadyrequest",
        },
      },
      {
        path: "/get-ready/analytics",
        component: lazy(() => import("@/app/apps/get-ready/analytics/page")),
        nav: { title: "Analytics", icon: BarChart2 },
        permission: {
          entityType: "dealership",
          action: "view_getreadyrequest",
        },
      },
      {
        path: "/get-ready/logs",
        component: lazy(
          () => import("@/app/apps/get-ready/getready-logs/page")
        ),
        nav: { title: "Logs", icon: ClipboardList },
        // Backend split logs onto their own Django auto-generated
        // codename in PR #158 (`enforce-entity-actions`) — gate the
        // logs route on it directly.
        permission: {
          entityType: "dealership",
          action: "view_getreadylog",
        },
      },
      // Management is placed last in the sidebar — it's a settings-style
      // page, so it lives below the day-to-day Get Ready / Analytics /
      // Logs trio.
      {
        path: "/get-ready/management",
        component: lazy(() => import("@/app/apps/get-ready/management/page")),
        nav: { title: "Management", icon: Settings },
        // Backend now uses Django's per-model auto-generated codenames
        // for Services / Announcements (PR #158 `enforce-entity-actions`)
        // and the new scoped-group ones for Internal Service Advisors.
        // Show the nav entry if the user has view access to ANY of the
        // three management surfaces — each tab still gates its own
        // write controls. `view_getreadyrequest` is kept as a legacy
        // fallback for accounts provisioned before the split.
        permission: {
          entityType: "dealership",
          action: [
            "view_getreadyservice",
            "view_getreadyannouncement",
            "view_getready_scopedgroupmembership",
            "view_getreadyrequest",
          ],
        },
      },
      {
        path: "/get-ready/create",
        component: lazy(() => import("@/app/apps/get-ready/create/page")),
        permission: {
          entityType: "dealership",
          action: "add_getreadyrequest",
        },
      },
    ],
  },
  cleanup: {
    key: "cleanup",
    name: "Cleanup",
    basePath: "/cleanup",
    plan: "Enterprise",
    routes: [
      {
        path: "/cleanup",
        component: lazy(() => import("@/app/apps/cleanup/page")),
        nav: { title: "Cleanup", icon: Car },
        permission: { entityType: "cleanup", action: "view_cleanup" },
      },
      {
        path: "/cleanup/analytics",
        component: lazy(() => import("@/app/apps/cleanup/analytics/page")),
        nav: { title: "Analytics & Reporting", icon: BarChart2 },
        permission: { entityType: "cleanup", action: "view_analytics" },
      },
      {
        path: "/cleanup/admin",
        component: lazy(() => import("@/app/apps/cleanup/admin/page")),
        nav: { title: "Administration", icon: Settings },
        permission: {
          entityType: "cleanup",
          action: "manage_cleanup",
          group: "superadmin",
        },
      },
      {
        path: "/cleanup/vehicle-profile",
        component: lazy(
          () => import("@/app/apps/cleanup/vehicle-profile/page")
        ),
        nav: { title: "Vehicle Profile", icon: CarFront },
        permission: { entityType: "cleanup", action: "view_vehicle" },
      },
      {
        path: "/cleanup/create",
        component: lazy(() => import("@/app/apps/cleanup/create/page")),
        permission: { entityType: "cleanup", action: "add_cleanup" },
      },
    ],
  },
};

export const statusColors = {
  // Get Ready statuses (camelCase, mirror GetReadyStatus enum values).
  readyForService: "bg-[#B9CDE5] text-[#062C70]",
  inProcess: "bg-[#FDE68A] text-[#92400E]",
  readyForCleanup: "bg-[#BBF7D0] text-[#166534]",
  cleanupOngoing: "bg-[#BBF7D0] text-[#166534]",
  cleanupDone: "bg-[#BBF7D0] text-[#166534]",
  cancelled: "bg-[#FECACA] text-[#991B1B]",
  // Cleanup app statuses — its own CleanupStatus enum still uses these.
  received: "bg-white ",
  complete: "bg-success ",
  duplicate: "bg-gray-500 ",
  // Garage Register statuses
  stocked_in: "bg-[#B9CDE5] text-[#062C70]",
  in_transit: "bg-[#D4E4F7] text-[#062C70]",
  awaiting_delivery: "bg-[#E8D5F5] text-[#4A1D6B]",
  stocked_out: "bg-[#FFE89C] ",
  delivered: "bg-[#B7F3C5] ",
  void: "bg-red-300 text-red-950",
} as const;

export const ROLES = [
  { label: "Developer", value: "Developer" },
  { label: "Designer", value: "Designer" },
  { label: "Manager", value: "Manager" },
  { label: "Analyst", value: "Analyst" },
  { label: "Specialist", value: "Specialist" },
];
